import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Card, Btn, Field, Select, Table } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface OrderWithAuth {
  id: string;
  code: string;
  fuelVariantName: string;
  fuelOctane: string;
  liters: number;
  total: number;
  stationName: string;
  slotDate: string;
  slotTime: string;
  status: string;
  authFile?: {
    key: string;
    name: string;
    status: 'pending' | 'approved' | 'rejected';
    note?: string | null;
    reviewerEmail?: string | null;
    uploadedAt: string;
    reviewedAt?: string | null;
  } | null;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendientes' },
  { value: '', label: 'Todos' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
];

function AuthPill({ s }: { s: NonNullable<OrderWithAuth['authFile']>['status'] }) {
  const palette =
    s === 'approved'
      ? { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' }
      : s === 'pending'
      ? { bg: 'color-mix(in oklab, oklch(0.78 0.16 70) 14%, transparent)', fg: 'oklch(0.78 0.16 70)' }
      : { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' };
  return (
    <span className="mono" style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 999, background: palette.bg, color: palette.fg, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {s}
    </span>
  );
}

export default function AuthFilesPage() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? 'pending';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data, isLoading } = useQuery<{ items: OrderWithAuth[]; total: number; limit: number }>({
    queryKey: ['admin', 'auth-files', status, page],
    queryFn: () => api('/api/admin/auth-files', { query: { status: status || undefined, page } }),
  });

  const setFilter = (next: Partial<{ status: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const cur = { status, page: String(page) };
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div>
      <PageHeader
        kicker="AUTORIZOS"
        title={`${data?.total ?? 0} archivos`}
        sub="Cargas > 60 L requieren autorizo. Revisalos y aprobá o rechazá con nota."
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <Select<string> label="Estado" value={status} onChange={(v) => setFilter({ status: v, page: '1' })} options={STATUS_OPTIONS} />
        </div>
      </div>

      {isLoading && <div style={{ color: 'var(--text-3)' }}>Cargando…</div>}

      {!isLoading && data?.items.length === 0 && (
        <Card>
          <div style={{ color: 'var(--text-3)' }}>
            {status === 'pending' ? 'Sin autorizos pendientes. 🎉' : 'Sin archivos en este filtro.'}
          </div>
        </Card>
      )}

      {status === 'pending' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data?.items.map((o) => <PendingCard key={o.id} order={o} onReviewed={() => qc.invalidateQueries({ queryKey: ['admin', 'auth-files'] })} />)}
        </div>
      ) : (
        <Table<OrderWithAuth>
          rows={data?.items ?? []}
          empty="—"
          columns={[
            { key: 'code', label: 'Orden', render: (r) => <Link to={`/orders/${r.id}`} className="mono" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, fontSize: 12 }}>{r.code}</Link> },
            { key: 'fuel', label: 'Carga', render: (r) => `${r.fuelVariantName} · ${r.liters} L` },
            { key: 'station', label: 'Estación', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.stationName}</span> },
            { key: 'total', label: 'Total', render: (r) => <span className="mono">${r.total.toFixed(2)}</span> },
            { key: 'status', label: 'Estado', render: (r) => r.authFile ? <AuthPill s={r.authFile.status} /> : '—', width: 110 },
            {
              key: 'reviewed',
              label: 'Revisado',
              render: (r) =>
                r.authFile?.reviewedAt ? (
                  <div>
                    <div className="mono" style={{ fontSize: 11 }}>{new Date(r.authFile.reviewedAt).toLocaleDateString('es-MX')}</div>
                    {r.authFile.reviewerEmail && <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{r.authFile.reviewerEmail}</div>}
                  </div>
                ) : (
                  <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>—</span>
                ),
            },
            {
              key: 'note',
              label: 'Nota',
              render: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.authFile?.note || '—'}</span>,
            },
          ]}
        />
      )}

      {data && totalPages > 1 && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {data.total} totales · página {page} de {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button disabled={page <= 1} onClick={() => setFilter({ page: String(page - 1) })} style={pager(page <= 1)}>←</button>
            <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })} style={pager(page >= totalPages)}>→</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingCard({ order, onReviewed }: { order: OrderWithAuth; onReviewed: () => void }) {
  const { can } = useAuth();
  const [note, setNote] = useState('');
  const canEdit = can('auth-files.edit');
  const review = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; note?: string }) =>
      api('/api/admin/auth-files/review', { body: { orderId: order.id, decision: input.decision, note: input.note } }),
    onSuccess: () => { setNote(''); onReviewed(); },
  });
  return (
    <Card>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{order.code}</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{order.fuelVariantName} · {order.liters} L</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>{order.stationName}</div>
          {order.authFile && (
            <>
              <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>{order.authFile.name}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                Subido {new Date(order.authFile.uploadedAt).toLocaleString('es-MX')}
              </div>
            </>
          )}
          <div style={{ marginTop: 10 }}>
            <Link to={`/orders/${order.id}`} style={{ color: 'var(--accent)', fontSize: 12, textDecoration: 'none' }}>Ver orden completa →</Link>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
          {canEdit ? (
            <>
              <Field label="Nota (opcional aprobando · requerida rechazando)" value={note} onChange={setNote} placeholder="Documento ilegible, etc." />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" onClick={() => review.mutate({ decision: 'approved', note: note || undefined })} disabled={review.isPending}>Aprobar</Btn>
                <Btn size="sm" variant="danger" onClick={() => review.mutate({ decision: 'rejected', note })} disabled={review.isPending || note.trim().length < 5}>Rechazar</Btn>
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>Sin permiso <span className="mono">auth-files.edit</span> para revisar.</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function pager(disabled: boolean): React.CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    color: disabled ? 'var(--text-3)' : 'var(--text)',
    width: 32, height: 32, borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  };
}
